import { computed, type Ref } from 'vue'
import { listItems, useBloxApi, useData } from '@bagelink/blox'
import { isPublicRecord } from '@/utils/publication'

/**
 * The record's own timestamps for an author page.
 *
 * A datastore page's `$page` context carries only the collection's content columns
 * — never the system ones — so `created_at` / `updated_at` are absent from the bound
 * record and have to be read from the list endpoint by slug.
 *
 * Deliberately no `select`: a field-selected row comes back with the database `id`
 * instead of the legacy `_id`, which `isPublicRecord` keys on.
 */
export function useAuthorDates(slug: Ref<string>, enabled: Ref<boolean>) {
	const api = useBloxApi()
	const { data } = useData<Record<string, any> | null>(
		() => `thetorah:author-dates:${slug.value}:${enabled.value ? 'on' : 'off'}`,
		async () => {
			if (!enabled.value || !slug.value) return null
			const fetchItems = api?.listItems ?? listItems
			const response = await fetchItems<Record<string, any>>('authors', {
				q: `slug eq "${slug.value}"`,
				limit: 1,
			})
			const rows: Record<string, any>[] = Array.isArray(response) ? response : response?.items ?? []
			return rows.find(row => isPublicRecord('authors', row)) ?? null
		},
		{ default: null },
	)
	return { dates: computed(() => data.value) }
}

/**
 * The date an author page displays: the record's last-updated stamp, falling back
 * to its creation stamp when the row has never been edited.
 */
export function formatRecordDate(row: Record<string, any> | null | undefined): string {
	const raw = row?.updated_at || row?.created_at
	if (typeof raw !== 'string' || !raw) return ''
	const date = new Date(raw)
	if (Number.isNaN(date.getTime())) return raw
	return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}
